import { useState } from 'react'
import { Card, ChargeList, CheckoutHeader, Checklist, RecommendationCard, Skeleton } from '@/ds'
import { LinkRow } from '@/app/components/LinkRow'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { formatMoney, time } from '@/lib/format'
import { useNow } from '@/lib/useNow'
import { useAppNavigate } from '@/lib/query-params'
import { richText } from '@/lib/richText'
import { asyncValue, useAsync } from '@/lib/useAsync'
import { checkoutService } from '@/services/checkout'
import type { Checkout } from '@/services/checkout'
import { fianzaService } from '@/services/fianza'
import { reservaService } from '@/services/reserva'
import { useApiLang } from '../Checkin/useSave'
import { DepositExplainer } from '../Estancia/sections/DepositCards'
import { SectionError } from '../Estancia/sections/SectionError'
import { BackLink } from '../Pagos/shared'
import { BOOKING_ROUTES, changeQuery, useLang } from '../Reserva/shared'
import { LeaveAction } from './LeaveAction'
import { CLOSE_ROUTES, longDate, remainingText } from './shared'

const RECEIPTS_ROUTE = '/reserva/recibos'

/**
 * 8.10 · Salida (Cierre.dc.html checkout, Bloque 7 §7a): hora y cuenta atrás, instrucciones marcables, "Ya hemos
 * salido", salida tardía por el cambio de reserva (D47), cargos con la factura del bloque 3 y qué pasa con la fianza
 * enlazando a su línea de tiempo (sin duplicarla).
 */
export function Salida() {
  const { t, lang } = useLang()
  const apiLang = useApiLang()
  const stay = useLoadedStay()
  const now = useNow(30000)
  const [data, reload] = useAsync(`salida:${stay.guestCode}:${apiLang}`, (signal) => checkoutService.getCheckout(stay, apiLang, signal))
  const [deposit] = useAsync(`salida-fianza:${stay.guestCode}:${apiLang}`, (signal) => fianzaService.getDeposit(stay, apiLang, signal))
  return (
    <main className="page">
      <div className="pagos__head">
        <BackLink to="/" label={t('v2.ds.nav.estancia')} />
        <h1>{t('v2.salida.titulo')}</h1>
        <p className="page__lead">{t('v2.salida.lead', { date: longDate(stay.dates.checkOut, lang) })}</p>
      </div>
      {data.kind === 'loading' && (
        <div className="pagos__stack" aria-busy="true">
          <Skeleton height={96} />
          <Skeleton height={220} />
        </div>
      )}
      {data.kind === 'error' && <SectionError code={data.code} onRetry={reload} />}
      {data.kind === 'ready' && <CheckoutContent checkout={data.value} now={now} />}
      <DepositExplainer deposit={deposit} lang={lang} origin="salida" />
      <LinkRow icon="calendar" title={t('v2.salida.despues')} subtitle={t('v2.salida.despuesSub')} to={CLOSE_ROUTES.after} />
    </main>
  )
}

function CheckoutContent({ checkout, now }: { checkout: Checkout; now: Date }) {
  const { t, lang } = useLang()
  const apiLang = useApiLang()
  const stay = useLoadedStay()
  const { go } = useAppNavigate()
  const [checked, setChecked] = useState<Record<string, boolean>>({})
  const [left, setLeft] = useState(checkout.left)
  const [options] = useAsync(`salida-cambios:${stay.guestCode}:${stay.dates.checkOut}`, (signal) => reservaService.getChangeOptions(stay, apiLang, signal))
  const late = asyncValue(options)?.options.find((o) => o.type === 'late_checkout' && o.allowed) ?? null
  const remaining = remainingText(checkout.time, now, t)
  const totalLabel = checkout.allPaid === true ? t('v2.salida.totalTodoPagado') : checkout.allPaid === false ? t('v2.salida.totalPendiente') : t('v2.salida.total')

  return (
    <>
      <CheckoutHeader time={time(checkout.time, lang)} {...(remaining ? { remaining } : {})} />
      <section className="pagos__group" aria-labelledby="salida-antes">
        <h2 id="salida-antes" className="pagos__h2">
          {t('v2.salida.antes')}
        </h2>
        {checkout.checklist.length > 0 && (
          <Checklist
            items={checkout.checklist.map((it) => ({ id: it.id, label: it.text, done: checked[it.id] ?? it.done }))}
            onToggle={(id, done) => {
              setChecked((c) => ({ ...c, [id]: done }))
              void checkoutService.setChecklistItem(stay, id, done)
            }}
          />
        )}
        {checkout.instructions && (
          <Card>
            <div className="cierre__card">
              <h3 className="cierre__card-title">{checkout.instructions.name}</h3>
              <div className="cierre__rich">{richText(checkout.instructions.description)}</div>
            </div>
          </Card>
        )}
        {checkout.checklist.length === 0 && !checkout.instructions && <p className="cierre__note">{t('v2.salida.sinInstrucciones')}</p>}
        <LeaveAction checkout={checkout} help={t('v2.salida.yaHemosSalidoAyuda')} onLeft={() => setLeft(true)} />
      </section>

      {late && late.time && late.price && !left && (
        <RecommendationCard
          reason={t('v2.salida.masTiempo')}
          title={t('v2.salida.salidaTardia', { time: late.time })}
          price={formatMoney(late.price, lang)}
          actionLabel={t('v2.peticiones.nueva.salirBoton')}
          onAction={() => go(BOOKING_ROUTES.pick('late_checkout'), { query: changeQuery('salida') })}
        />
      )}

      {checkout.total && (
        <section className="pagos__group" aria-labelledby="salida-cargos">
          <h2 id="salida-cargos" className="pagos__h2">
            {t('v2.salida.cargos')}
          </h2>
          <Card flush>
            <ChargeList items={checkout.charges.map((c) => ({ id: c.id, concept: c.concept, amount: formatMoney(c.amount, lang) }))} totalLabel={totalLabel} total={formatMoney(checkout.total, lang)} />
          </Card>
          <LinkRow icon="file-text" title={t('v2.salida.facturaRecibos')} to={RECEIPTS_ROUTE} />
        </section>
      )}
    </>
  )
}
